import React, { useState } from 'react';
import * as S from './styles';
import { Topbar, Navbar, DashboardFooter, Breadcrumb } from '../../components';
import { usePermissions, useStorage } from '../../hooks';
import { profileFields } from '../../utils/mocks/profile';
import refresh from '../../assets/dashboard-icons/refresh.png';
import { sendNotification } from '../../utils/functions';

export const Profile: React.FC = () => {
  const { getItemOnStorage, setItemOnStorage } = useStorage();
  const [{ isLoading, error }, , , , , , , updateUserCode] = usePermissions();

  const [user, setUser] = useState<any>(() => {
    try {
      const storedUser = getItemOnStorage('currentUser');
      return storedUser ? JSON.parse(storedUser) : {};
    } catch {
      return {};
    }
  });

  const profileName = typeof user?.perfil === 'string' ? user.perfil : '';
  const profileConfig = profileFields.find(
    profile => profile.name === profileName,
  );
  //nome e email como default, caso não tenha perfil configurado, para não quebrar a tela
  const defaultFields = ['nome', 'email'];

  const fieldsToRender = [
    ...new Set([...defaultFields, ...(profileConfig?.fields ?? [])]),
  ];
  const isRevendedor = profileName === 'revendedor-allied';

  const handleUpdateUserCode = async () => {
    try {
      const response = await updateUserCode(user.userEmail);
      if (response) {
        const vendorCode = {
          ...(isRevendedor
            ? { vendedorAdicional: response?.sellerCode, vendedor: '' }
            : { vendedor: response?.sellerCode }),
        };
        setUser((prevUser: any) => ({
          ...prevUser,
          ...vendorCode,
        }));
        const updatedCurrentUser = {
          ...user,
          ...vendorCode,
        };

        setItemOnStorage('currentUser', JSON.stringify(updatedCurrentUser));
        sendNotification('Código atualizado com sucesso!', {
          type: 'success',
        });
      }
    } catch (error: any) {
      sendNotification('Erro ao atualizar código: ' + error, {
        type: 'error',
      });
    }
  };

  return (
    <S.Container>
      <Topbar />
      <Navbar />
      <S.Content>
        <Breadcrumb paths={paths} />
        <S.Title>Meu Perfil</S.Title>
        <S.ContentContainer>
          <S.Section>
            <S.SectionTitle>Dados Pessoais</S.SectionTitle>
            {fieldsToRender.includes('nome') && (
              <S.Info>
                <S.Label>Nome:</S.Label>
                <S.Value>{user?.user ?? user?.nome ?? '-'}</S.Value>
              </S.Info>
            )}
            {fieldsToRender.includes('email') && (
              <S.Info>
                <S.Label>Email:</S.Label>
                <S.Value>{user?.userEmail ?? user?.email ?? '-'}</S.Value>
              </S.Info>
            )}
            {fieldsToRender.includes('cpfCnpj') && (
              <S.Info>
                <S.Label>CPF/CNPJ:</S.Label>
                <S.Value>
                  {typeof user?.cnpj === 'string' && user.cnpj
                    ? user.cnpj.replace(
                        /^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/,
                        '$1.$2.$3/$4-$5',
                      )
                    : '-'}
                </S.Value>
              </S.Info>
            )}
            {fieldsToRender.includes('canaisResponsaveis') && (
              <S.Info>
                <S.Label>Canais Responsáveis:</S.Label>
                <S.Value>
                  {Array.isArray(user?.canaisResponsaveis) &&
                  user.canaisResponsaveis.length > 0
                    ? user.canaisResponsaveis.join(', ')
                    : '-'}
                </S.Value>
              </S.Info>
            )}
            {fieldsToRender.includes('grupoEconomico') && (
              <S.Info>
                <S.Label>Grupo Econômico:</S.Label>
                <S.Value>
                  {Array.isArray(user?.gruposEconomicos) &&
                  user.gruposEconomicos.length > 0
                    ? user.gruposEconomicos[0]
                    : '-'}
                </S.Value>
              </S.Info>
            )}
          </S.Section>

          <S.Section>
            {(fieldsToRender.includes('vendedor') ||
              fieldsToRender.includes('vendedorAdicional') ||
              fieldsToRender.includes('dadosBancarios')) && (
              <>
                <S.SectionTitle>Dados Comerciais</S.SectionTitle>

                {fieldsToRender.includes('vendedor') && (
                  <S.Info>
                    <S.Label style={{ marginRight: '10px' }}>
                      Código de Vendedor:
                    </S.Label>
                    <S.Value className="vendor">
                      {user?.vendedor ?? '-'}
                    </S.Value>
                    <S.Buttons.IconButton
                      onClick={handleUpdateUserCode}
                      disabled={isLoading}
                    >
                      <S.Icon src={refresh} alt="Atualizar" />
                      {isLoading ? 'Atualizando...' : 'Atualizar'}
                    </S.Buttons.IconButton>
                  </S.Info>
                )}

                {fieldsToRender.includes('vendedorAdicional') && (
                  <S.Info>
                    <S.Label style={{ marginRight: '10px' }}>
                      Cod. Vendedor Adicional:
                    </S.Label>
                    <S.Value>{user?.vendedorAdicional ?? '-'}</S.Value>
                  </S.Info>
                )}

                {fieldsToRender.includes('dadosBancarios') && user?.agencia && (
                  <S.Info>
                    <S.Label>Dados Bancários:</S.Label>
                    <S.Value>
                      {user?.banco} - Ag. {user?.agencia} - CC.:{' '}
                      {user?.contaCorrenteDigito}
                    </S.Value>
                  </S.Info>
                )}
              </>
            )}
          </S.Section>

          <S.Section>
            {(fieldsToRender.includes('razaoSocial') ||
              fieldsToRender.includes('nomeFantasia') ||
              fieldsToRender.includes('inscricaoEstadual')) && (
              <>
                <S.SectionTitle>Empresa</S.SectionTitle>

                {fieldsToRender.includes('razaoSocial') && (
                  <S.Info>
                    <S.Label>Razão Social:</S.Label>
                    <S.Value>{user?.nome ?? '-'}</S.Value>
                  </S.Info>
                )}

                {fieldsToRender.includes('nomeFantasia') && (
                  <S.Info>
                    <S.Label>Nome Fantasia:</S.Label>
                    <S.Value>{user?.nomeFantasia ?? '-'}</S.Value>
                  </S.Info>
                )}

                {fieldsToRender.includes('inscricaoEstadual') && (
                  <S.Info>
                    <S.Label>Inscrição Estadual:</S.Label>
                    <S.Value>{user?.inscricaoEstadual ?? '-'}</S.Value>
                  </S.Info>
                )}
              </>
            )}
          </S.Section>

          <S.Section>
            {(fieldsToRender.includes('telefone') ||
              fieldsToRender.includes('endereco')) && (
              <>
                <S.SectionTitle>Contato & Endereço</S.SectionTitle>

                {fieldsToRender.includes('telefone') && (
                  <S.Info>
                    <S.Label>Telefone:</S.Label>
                    <S.Value>
                      {typeof user?.telefone === 'string' && user.telefone
                        ? user.telefone.replace(
                            /^([0-9]{2})([0-9]{4})([0-9]{4})$/,
                            '($1) $2-$3',
                          )
                        : '-'}
                    </S.Value>
                  </S.Info>
                )}

                {fieldsToRender.includes('endereco') && (
                  <S.Info>
                    <S.Label>Endereço:</S.Label>
                    <S.Value>
                      {[
                        user?.endereco,
                        user?.bairro,
                        user?.uf,
                        user?.municipio,
                        user?.cep,
                      ]
                        .filter(Boolean)
                        .join(' - ') || '-'}
                    </S.Value>
                  </S.Info>
                )}
              </>
            )}
          </S.Section>
        </S.ContentContainer>
      </S.Content>
      <DashboardFooter />
    </S.Container>
  );
};

const paths = [
  {
    title: 'Meu Painel',
    link: '/painel',
  },
  {
    title: 'Meu Perfil',
    link: '/perfil',
  },
];
